import {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
  type ReactNode,
} from 'react';
import {
  onAuthStateChanged,
  signInWithPhoneNumber,
  signInWithPopup,
  signInAnonymously,
  GoogleAuthProvider,
  OAuthProvider,
  signOut as firebaseSignOut,
  RecaptchaVerifier,
  type User as FirebaseUser,
  type ConfirmationResult,
} from 'firebase/auth';
import { getFirebaseAuth } from '@/lib/firebase';
import { api } from '@/lib/api';
import { trackSignupConversion } from '@/lib/ads';
import { tagDemoSession } from '@/lib/funnel';

interface Workshop {
  id: string;
  name: string;
  role: string;
  isCurrent: boolean;
  /** O dono da oficina (Fase 1 das permissões). */
  isOwner?: boolean;
  /** O que a pessoa pode fazer NESTA oficina — o efetivo, calculado pela API. */
  permissions?: string[];
}

interface AppUser {
  id: string;
  firebaseUid: string;
  name: string;
  role: string;
  workshopId: string | null;
  activeWorkshopId?: string | null;
  workshopName?: string;
  workshops?: Workshop[];
  /**
   * Permissões por membro concedidas pelo gestor. `canViewFinance` estende o
   * acesso ao financeiro a quem não é gestor — a API é a autoridade
   * (requireFinanceAccess); aqui serve só para não oferecer no menu uma tela
   * que responderia 403.
   */
  permissions?: { canViewFinance?: boolean };
}

interface AuthState {
  firebaseUser: FirebaseUser | null;
  user: AppUser | null;
  workshops: Workshop[];
  loading: boolean;
  sendOTP: (phone: string) => Promise<void>;
  confirmOTP: (code: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signInWithApple: () => Promise<void>;
  signInDemo: () => Promise<void>;
  register: (data: { name: string; workshopName: string }) => Promise<void>;
  joinWorkshop: (data: { name: string; code: string }) => Promise<{ role: string; name: string }>;
  signOut: () => Promise<void>;
  switchWorkshop: (workshopId: string) => Promise<void>;
  otpSent: boolean;
  needsRegistration: boolean;
  /** Sessão de demonstração read-only (visitante anônimo do Firebase). */
  isDemo: boolean;
  isImpersonating: boolean;
  endImpersonation: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [user, setUser] = useState<AppUser | null>(null);
  const [workshops, setWorkshops] = useState<Workshop[]>([]);
  const [loading, setLoading] = useState(true);
  const [otpSent, setOtpSent] = useState(false);
  const [needsRegistration, setNeedsRegistration] = useState(false);
  const [isImpersonating, setIsImpersonating] = useState(() => !!sessionStorage.getItem('impersonationToken'));
  const confirmationRef = useRef<ConfirmationResult | null>(null);
  const recaptchaRef = useRef<RecaptchaVerifier | null>(null);

  const fetchUser = useCallback(async () => {
    try {
      const res = await api.get('/me');
      const u = res.data.user;
      const userWorkshops = u.workshops ?? [];
      setUser(u);
      setWorkshops(userWorkshops);
      // F24 hotfix: needsRegistration era true SOMENTE em /me failure.
      // User b2c_customer (sem workshop) com /me 200 caia em
      // needsRegistration=false, dashboard renderizava, e os endpoints
      // internos retornavam 400 "No workshop associated" sem o
      // onboarding aparecer. Agora: tambem dispara quando user existe
      // mas nao tem nenhum workshop (workshopId nulo + activeWorkshopId
      // nulo + workshops[] vazio).
      const hasAnyWorkshop = !!(u.workshopId || u.activeWorkshopId) || userWorkshops.length > 0;
      setNeedsRegistration(!hasAnyWorkshop);
    } catch {
      setUser(null);
      setWorkshops([]);
      setNeedsRegistration(true);
    }
  }, []);

  useEffect(() => {
    // Indicação oficina→oficina (growth 8.1): ?ref=CODE vem do link do site.
    // Persiste até o cadastro concluir — o fluxo passa por login/OTP antes
    // do POST /workshops, então sessionStorage não sobreviveria a redirects.
    try {
      const ref = new URLSearchParams(window.location.search).get('ref');
      if (ref) localStorage.setItem('mustauto:ref', ref);
    } catch { /* storage indisponível — segue sem ref */ }

    // Check for impersonation token in URL
    const params = new URLSearchParams(window.location.search);
    const impToken = params.get('impersonationToken');
    if (impToken) {
      // Remove token from URL
      params.delete('impersonationToken');
      const newUrl = params.toString()
        ? `${window.location.pathname}?${params.toString()}`
        : window.location.pathname;
      window.history.replaceState({}, '', newUrl);

      // Verify and activate impersonation
      api
        .get('/backoffice/impersonation/verify', { params: { token: impToken } })
        .then((res) => {
          const { user: impUser, workshop } = res.data as {
            user: { id: string; name: string; role: string | null };
            workshop: { id: string; name: string };
          };
          sessionStorage.setItem('impersonationToken', impToken);
          setUser({
            id: impUser.id,
            firebaseUid: '',
            name: impUser.name,
            role: impUser.role ?? 'manager',
            workshopId: workshop.id,
            activeWorkshopId: workshop.id,
            workshopName: workshop.name,
            workshops: [{ id: workshop.id, name: workshop.name, role: 'manager', isCurrent: true }],
          });
          setWorkshops([{ id: workshop.id, name: workshop.name, role: 'manager', isCurrent: true }]);
          setIsImpersonating(true);
          setNeedsRegistration(false);
          setLoading(false);
        })
        .catch(() => {
          // Invalid token, proceed with normal auth
          setLoading(false);
        });
      return;
    }

    // If already impersonating (session restore), don't use Firebase
    if (sessionStorage.getItem('impersonationToken')) {
      setLoading(false);
      return;
    }

    const auth = getFirebaseAuth();
    const unsub = onAuthStateChanged(auth, async (fbUser) => {
      setFirebaseUser(fbUser);
      if (fbUser) {
        // Sessão de demonstração (anônima): marca os analytics para que o
        // demo seja segmentado/filtrado fora das métricas de negócio.
        if (fbUser.isAnonymous) tagDemoSession();
        await fetchUser();
      } else {
        setUser(null);
        setWorkshops([]);
        setNeedsRegistration(false);
      }
      setLoading(false);
    });
    return unsub;
  }, [fetchUser]);

  const sendOTP = async (phone: string) => {
    const auth = getFirebaseAuth();

    // Limpa recaptcha anterior se existir
    if (recaptchaRef.current) {
      recaptchaRef.current.clear();
      recaptchaRef.current = null;
    }

    recaptchaRef.current = new RecaptchaVerifier(auth, 'recaptcha-container', {
      size: 'invisible',
    });

    const confirmation = await signInWithPhoneNumber(
      auth,
      phone,
      recaptchaRef.current,
    );
    confirmationRef.current = confirmation;
    setOtpSent(true);
  };

  const confirmOTP = async (code: string) => {
    if (!confirmationRef.current) {
      throw new Error('Envie o código primeiro.');
    }
    await confirmationRef.current.confirm(code);
    confirmationRef.current = null;
    setOtpSent(false);
  };

  const signInWithGoogle = async () => {
    const auth = getFirebaseAuth();
    const provider = new GoogleAuthProvider();
    await signInWithPopup(auth, provider);
    // onAuthStateChanged → fetchUser → needsRegistration decide o próximo passo.
  };

  const signInWithApple = async () => {
    const auth = getFirebaseAuth();
    const provider = new OAuthProvider('apple.com');
    provider.addScope('email');
    provider.addScope('name');
    await signInWithPopup(auth, provider);
  };

  const signInDemo = async () => {
    // Login anônimo do Firebase. O backend (com DEMO_MODE_ENABLED) mapeia o
    // token anônimo para a oficina-demo read-only. onAuthStateChanged →
    // fetchUser cuida do resto (navega para o dashboard).
    const auth = getFirebaseAuth();
    await signInAnonymously(auth);
  };

  const register = async (data: { name: string; workshopName: string }) => {
    // 1. Cria o registro do usuário. Usa 'b2c_customer' (papel sem gate de
    //    invite) — a promoção a 'manager' acontece em POST /workshops, que
    //    é o único caminho legítimo de virar manager sem convite (F48).
    await api.post('/auth/session', {
      name: data.name,
      phone: firebaseUser?.phoneNumber ?? '',
      role: 'b2c_customer' as const,
    });

    // 2. Só cria a oficina se o usuário ainda não tem uma (evita duplicar
    //    uma oficina criada antes via mobile quando needsRegistration foi
    //    disparado por erro transitório do /me). CNPJ é coletado no wizard.
    let hasWorkshop = false;
    try {
      await api.get('/workshops/my');
      hasWorkshop = true;
    } catch {
      hasWorkshop = false;
    }

    if (!hasWorkshop) {
      let referralCode: string | null = null;
      try { referralCode = localStorage.getItem('mustauto:ref'); } catch { /* sem ref */ }
      await api.post('/workshops', {
        name: data.workshopName,
        ...(referralCode ? { referralCode } : {}),
      });
      try { localStorage.removeItem('mustauto:ref'); } catch { /* ok */ }
      // Conversão do Google Ads: oficina recém-criada (cadastro concluído).
      trackSignupConversion();
    }

    // 3. Refetch user with workshopId populated
    await fetchUser();
  };

  const joinWorkshop = async (data: { name: string; code: string }) => {
    // Co-gestor entrando em oficina existente por convite. Garante o registro
    // do usuário e então vincula via POST /workshops/join (papel vem do convite).
    await api.post('/auth/session', {
      name: data.name,
      phone: firebaseUser?.phoneNumber ?? '',
      role: 'b2c_customer' as const,
    });
    const res = await api.post('/workshops/join', { code: data.code });
    await fetchUser();
    return res.data as { role: string; name: string };
  };

  const switchWorkshop = useCallback(async (workshopId: string) => {
    await api.post('/me/workshops/switch', { workshopId });
    await fetchUser();
    window.location.reload();
  }, [fetchUser]);

  const endImpersonation = useCallback(() => {
    sessionStorage.removeItem('impersonationToken');
    setIsImpersonating(false);
    setUser(null);
    setWorkshops([]);
    window.close();
  }, []);

  const signOut = async () => {
    const auth = getFirebaseAuth();
    await firebaseSignOut(auth);
    sessionStorage.removeItem('impersonationToken');
    setIsImpersonating(false);
    setUser(null);
    setWorkshops([]);
    setOtpSent(false);
    setNeedsRegistration(false);
    confirmationRef.current = null;
  };

  return (
    <AuthContext.Provider
      value={{ firebaseUser, user, workshops, loading, sendOTP, confirmOTP, signInWithGoogle, signInWithApple, signInDemo, register, joinWorkshop, signOut, switchWorkshop, otpSent, needsRegistration, isDemo: firebaseUser?.isAnonymous ?? false, isImpersonating, endImpersonation }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
